import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Facebook,
  Instagram as InstagramIcon,
  Linkedin,
  Loader2,
  ArrowLeft,
  Wand2,
  Check,
  AlertCircle,
  Clock,
  Lock,
  LockOpen,
  Plus,
  CheckCircle2,
  Youtube,
  MapPin,
  Pin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import GlassCard from "@/components/GlassCard";
import PageHeader from "@/components/PageHeader";
import GradientMesh from "@/components/GradientMesh";
import CustomerConnectionDialog from "@/components/CustomerConnectionDialog";
import AdminConnectionsPanel from "@/components/AdminConnectionsPanel";
import AdminUnlockDialog from "@/components/AdminUnlockDialog";
import { startLinkedInOAuth } from "@/lib/linkedinOAuth";

// X / Twitter glyph
function XGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.97 6.817H1.673l7.73-8.835L1.247 2.25h6.835l4.713 6.231 5.449-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

// TikTok glyph (custom SVG — Coming Soon roadmap card only, no
// integration behavior wired up).
function TikTokGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  );
}

const ICONS = {
  facebook: Facebook,
  instagram: InstagramIcon,
  x: XGlyph,
  tiktok: TikTokGlyph,
  linkedin: Linkedin,
  youtube: Youtube,
  gbp: MapPin,
  pinterest: Pin,
};

// Client-only Coming Soon roadmap cards for upcoming platform
// integrations. These are additive display cards shown on the
// Connections page ONLY. They have no backend, no OAuth flow, no
// publishing path, and MUST NOT count toward any plan's platform
// limits or quota logic. `coming_soon: true` triggers the existing
// disabled-state UI and excludes them from `activeCount` (see line
// where activeCount is computed).
const UPCOMING_INTEGRATIONS = [
  {
    platform: "tiktok",
    label: "TikTok",
    icon: "tiktok",
    color: "#EE1D52",
    description: "Publish short-form video content to TikTok.",
    connected: false,
    configured: false,
    coming_soon: true,
    customer_status_message:
      "TikTok is on the Postmoro roadmap. It doesn't count against your plan's platform slots and can't be connected yet.",
  },
  {
    platform: "youtube",
    label: "YouTube",
    icon: "youtube",
    color: "#FF0000",
    description: "Publish video content and Shorts to YouTube.",
    connected: false,
    configured: false,
    coming_soon: true,
    customer_status_message:
      "YouTube is on the Postmoro roadmap. It doesn't count against your plan's platform slots and can't be connected yet.",
  },
  {
    platform: "gbp",
    label: "Google Business Profile",
    icon: "gbp",
    color: "#4285F4",
    description: "Post updates, offers, and events to your Google Business Profile.",
    connected: false,
    configured: false,
    coming_soon: true,
    customer_status_message:
      "Google Business Profile is on the Postmoro roadmap. It doesn't count against your plan's platform slots and can't be connected yet.",
  },
  {
    platform: "pinterest",
    label: "Pinterest",
    icon: "pinterest",
    color: "#E60023",
    description: "Publish Pins and boards to your Pinterest business account.",
    connected: false,
    configured: false,
    coming_soon: true,
    customer_status_message:
      "Pinterest is on the Postmoro roadmap. It doesn't count against your plan's platform slots and can't be connected yet.",
  },
];

const SUPPORTED_FOR_CUSTOMER_CONNECT = new Set(["facebook", "instagram", "x", "linkedin"]);

// Phase 17 platform-slot helpers -----------------------------------------
const PLATFORM_LABEL = {
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X / Twitter",
  linkedin: "LinkedIn",
};

function planLabel(plan) {
  const p = (plan || "free").toLowerCase();
  return p.charAt(0).toUpperCase() + p.slice(1);
}

/**
 * Phase 17c redesign — a real "choose your platforms" panel with visible
 * slot cards. Free/Starter users see two (or one) tangible slot cards
 * they can fill by picking a platform. Pro/Business see the unlimited
 * confirmation. The pinned/needs_selection state uses the same slot
 * grid but with an amber "action required" tone so it's obvious the
 * user MUST pick before publishing resumes.
 */
function PlatformSlotStrip({ slotCtx, navigate, onManage, onChoosePlatform, platforms }) {
  if (!slotCtx) return null;
  const {
    platform_limit: cap,
    unlimited,
    active_platforms,
    plan,
    needs_selection,
    connected_platforms,
  } = slotCtx;

  if (unlimited) {
    return (
      <div
        className="mt-5 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-3.5 flex items-center gap-2 text-sm"
        data-testid="platform-slots-unlimited"
      >
        <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <span className="font-medium text-[color:var(--text-1)]">All platforms included</span>
        <span className="text-[color:var(--text-3)]">
          — {planLabel(plan)} lets you publish to every supported network.
        </span>
      </div>
    );
  }

  const activeSet = new Set(active_platforms || []);
  const connectedSet = new Set(connected_platforms || []);
  const displayList = [...activeSet];
  while (displayList.length < (cap || 1)) displayList.push(null);

  const isActionNeeded = !!needs_selection;
  const selectedCount = activeSet.size;

  return (
    <div
      className={
        "mt-5 rounded-xl border p-4 sm:p-5 " +
        (isActionNeeded
          ? "border-[color:rgba(255,138,61,0.55)] bg-[color:rgba(255,138,61,0.06)]"
          : "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]")
      }
      data-testid={isActionNeeded ? "platform-slots-needs-selection" : "platform-slots-panel"}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="font-display text-base sm:text-lg font-semibold text-[color:var(--text-1)]">
            {isActionNeeded
              ? `Choose your ${cap} ${planLabel(plan)} platform${cap === 1 ? "" : "s"}`
              : `Choose your ${cap} ${planLabel(plan)} platform${cap === 1 ? "" : "s"}`}
          </div>
          <p className="text-sm text-[color:var(--text-2)] mt-1 leading-relaxed">
            {isActionNeeded
              ? `${planLabel(plan)} includes ${cap} active social platform${cap === 1 ? "" : "s"}. Choose the ${cap === 1 ? "one" : "two"} platform${cap === 1 ? "" : "s"} you want Postmoro to create and publish for. Your connected accounts stay saved.`
              : `${planLabel(plan)} includes any ${cap} supported social platform${cap === 1 ? "" : "s"}. Connect the platform${cap === 1 ? "" : "s"} you want Postmoro to create and publish for.`}
          </p>
        </div>
        <div className="text-xs whitespace-nowrap text-[color:var(--text-3)] mt-1 shrink-0">
          {selectedCount} of {cap} selected
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {displayList.slice(0, cap || 1).map((canonKey, idx) => (
          <SlotCard
            key={idx}
            index={idx + 1}
            canonKey={canonKey}
            platforms={platforms}
            connectedSet={connectedSet}
            onChoose={() => onChoosePlatform(idx, canonKey)}
          />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-[color:var(--text-3)]">
        <span>Facebook and Instagram each use one platform slot.</span>
        <button
          type="button"
          onClick={() => navigate("/billing")}
          className="text-[color:var(--brand-primary)] hover:underline font-medium"
          data-testid="platform-slots-upgrade"
        >
          Upgrade to Pro for all platforms →
        </button>
      </div>

      {slotCtx?.platform_change?.applies ? (
        <div
          className={
            "mt-3 rounded-md border px-3 py-2 text-[11px] leading-relaxed flex items-start gap-2 " +
            (slotCtx.platform_change.used
              ? "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] text-[color:var(--text-2)]"
              : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)]")
          }
          data-testid={
            slotCtx.platform_change.used
              ? "platform-change-badge-used"
              : "platform-change-badge-remaining"
          }
        >
          {slotCtx.platform_change.used ? (
            <Lock className="h-3 w-3 mt-0.5 shrink-0 text-[color:var(--accent-warm)]" />
          ) : (
            <Check className="h-3 w-3 mt-0.5 shrink-0 text-[color:var(--brand-primary)]" />
          )}
          <span>
            {slotCtx.platform_change.used ? (
              <>
                Platform change already used this billing cycle. You can change platforms
                again on{" "}
                <span className="font-semibold text-[color:var(--text-1)]">
                  {(() => {
                    const iso = slotCtx.platform_change.next_reset_at;
                    if (!iso) return "your next renewal";
                    try {
                      const d = new Date(iso);
                      return Number.isNaN(d.getTime())
                        ? "your next renewal"
                        : d.toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          });
                    } catch (_e) {
                      return "your next renewal";
                    }
                  })()}
                </span>
                .
              </>
            ) : (
              <>
                <span className="font-semibold text-[color:var(--text-1)]">
                  {(() => {
                    const rem = slotCtx.platform_change.remaining;
                    if (typeof rem !== "number") return "1 platform change remaining";
                    if (rem === 0) return "No platform changes remaining";
                    if (rem === 1) return "1 platform change remaining";
                    return `${rem} platform changes remaining`;
                  })()}
                </span>{" "}
                this billing cycle. Resets on{" "}
                {(() => {
                  const iso = slotCtx.platform_change.next_reset_at;
                  if (!iso) return "your next renewal";
                  try {
                    const d = new Date(iso);
                    return Number.isNaN(d.getTime())
                      ? "your next renewal"
                      : d.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        });
                  } catch (_e) {
                    return "your next renewal";
                  }
                })()}
                .
              </>
            )}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function SlotCard({ index, canonKey, platforms, connectedSet, onChoose }) {
  const label = canonKey ? (PLATFORM_LABEL[canonKey] || canonKey) : null;
  const isConnected = canonKey ? connectedSet.has(canonKey) : false;
  return (
    <div
      className={
        "rounded-lg border p-3.5 flex items-center gap-3 " +
        (canonKey
          ? "border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.06)]"
          : "border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]")
      }
      data-testid={`platform-slot-${index}${canonKey ? `-${canonKey}` : "-empty"}`}
    >
      <div
        className={
          "h-9 w-9 rounded-md flex items-center justify-center shrink-0 " +
          (canonKey
            ? "bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)]"
            : "bg-[color:var(--surface-3)] text-[color:var(--text-3)]")
        }
      >
        {canonKey ? <CheckCircle2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
          Platform {index}
        </div>
        <div className="text-sm font-semibold text-[color:var(--text-1)] truncate">
          {label || "Not selected"}
        </div>
        {canonKey ? (
          <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
            {isConnected ? "Connected · Active" : "Selected · Connection pending"}
          </div>
        ) : null}
      </div>
      <Button
        size="sm"
        variant={canonKey ? "outline" : "default"}
        onClick={onChoose}
        className={
          canonKey
            ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
            : "bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
        }
        data-testid={`platform-slot-${index}-${canonKey ? "replace" : "choose"}`}
      >
        {canonKey ? "Replace" : "Choose platform"}
      </Button>
    </div>
  );
}

/**
 * Choose Platform selector — appears when the user clicks "Choose
 * platform" on an empty slot. Shows all supported networks with their
 * true availability so the user knows exactly what will happen when
 * they click. No new OAuth flows — we defer to the existing per-
 * platform connect handler.
 */
function ChoosePlatformDialog({ open, onOpenChange, platforms, slotCtx, onConnect, onActivate }) {
  if (!open) return null;
  const active = new Set(slotCtx?.active_platforms || []);
  const supported = ["facebook", "instagram", "x", "linkedin"];
  const byPlatform = Object.fromEntries(
    (platforms || []).map((p) => [(p.platform || p.icon || "").toLowerCase(), p])
  );

  const rowFor = (canon) => {
    const p = byPlatform[canon] || byPlatform[canon === "x" ? "x" : canon] || null;
    const isActive = active.has(canon);
    const isConnected = !!p?.connected;
    const awaiting = !!p?.awaiting_app_review;

    let badge = "Connect";
    let badgeCls = "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)]";
    let handler = () => onConnect(p);
    if (isActive) {
      badge = "Already selected";
      badgeCls = "bg-[color:var(--surface-3)] text-[color:var(--text-3)]";
      handler = null;
    } else if (awaiting) {
      badge = "Pending Meta approval";
      badgeCls = "bg-[color:rgba(255,138,61,0.14)] text-[color:var(--accent-warm)] border border-[color:rgba(255,138,61,0.35)]";
      handler = null;
    } else if (isConnected) {
      badge = "Activate";
      badgeCls = "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)]";
      handler = () => onActivate(canon);
    }

    return (
      <button
        key={canon}
        type="button"
        disabled={!handler}
        onClick={handler || undefined}
        data-testid={`choose-platform-${canon}`}
        className={
          "w-full flex items-center justify-between px-3.5 py-3 rounded-md border transition text-left " +
          (handler
            ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:border-[color:var(--border-strong)]"
            : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] opacity-70 cursor-not-allowed")
        }
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[color:var(--text-1)]">
            {PLATFORM_LABEL[canon] || canon}
          </div>
          <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
            {awaiting
              ? "Facebook/Instagram publishing enables after Meta review approves."
              : isConnected
              ? "Connected · click to make this slot active"
              : "Not connected · click to start OAuth"}
          </div>
        </div>
        <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${badgeCls}`}>
          {badge}
        </span>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={() => onOpenChange(false)}
      data-testid="choose-platform-dialog"
    >
      <div
        className="w-full max-w-md bg-[color:var(--bg-1)] border border-[color:var(--border-subtle)] rounded-xl p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
          Choose a platform
        </div>
        <p className="text-sm text-[color:var(--text-2)] mt-1.5">
          Pick which social network to use for this slot. Facebook and Instagram each use one slot.
        </p>
        <div className="mt-4 space-y-2">
          {supported.map(rowFor)}
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
            data-testid="choose-platform-close"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Human-friendly render of an ISO timestamp for reset-date copy.
 * Falls back to a generic phrase when parsing fails so we never show a
 * broken date to a customer.
 */
function formatResetDate(iso) {
  if (!iso) return "your next renewal";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "your next renewal";
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (_e) {
    return "your next renewal";
  }
}

/**
 * Phase 17d — confirmation dialog shown BEFORE a Starter user consumes
 * their once-per-billing-cycle platform-change allowance. Lists the
 * current active set, the proposed new set, the reset date, and an
 * explicit warning. Requires an affirmative click on "Confirm platform
 * change" to proceed. Confirm/Close returns a boolean via ``onResolve``.
 */
function ConfirmPlatformChangeDialog({
  open,
  onCancel,
  onConfirm,
  saving,
  currentPlatforms,
  proposedPlatforms,
  nextResetAt,
  addingPlatform,
}) {
  if (!open) return null;
  const labelOf = (k) => PLATFORM_LABEL[k] || k;
  const reset = formatResetDate(nextResetAt);
  // If the caller passed `addingPlatform` (OAuth-start flow), the
  // "new active set" isn't fully specified yet (the user hasn't picked
  // which committed platform to drop). Render an "adding X" summary
  // instead of a misleading truncated list.
  const isOAuthAdd = !!addingPlatform && (proposedPlatforms || []).length <= 1;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
      onClick={onCancel}
      data-testid="confirm-platform-change-dialog"
    >
      <div
        className="w-full max-w-md bg-[color:var(--bg-1)] border border-[color:var(--border-subtle)] rounded-xl p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md flex items-center justify-center shrink-0 bg-[color:rgba(255,138,61,0.14)] text-[color:var(--accent-warm)]">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
              Confirm platform change
            </div>
            <p className="text-sm text-[color:var(--text-2)] mt-1 leading-relaxed">
              This uses your <span className="font-semibold">one platform change</span> for this
              billing cycle. You can change platforms again on{" "}
              <span className="font-semibold text-[color:var(--text-1)]">{reset}</span>.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div
            className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
            data-testid="confirm-platform-change-current"
          >
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
              Currently active
            </div>
            <ul className="mt-1.5 space-y-1">
              {(currentPlatforms || []).length === 0 ? (
                <li className="text-sm text-[color:var(--text-3)]">(none)</li>
              ) : (
                (currentPlatforms || []).map((k) => (
                  <li key={k} className="text-sm text-[color:var(--text-1)] font-medium">
                    {labelOf(k)}
                  </li>
                ))
              )}
            </ul>
          </div>
          <div
            className="rounded-lg border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.06)] p-3"
            data-testid="confirm-platform-change-new"
          >
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--brand-primary)]">
              {isOAuthAdd ? "Adding" : "New active set"}
            </div>
            <ul className="mt-1.5 space-y-1">
              {isOAuthAdd ? (
                <li className="text-sm text-[color:var(--text-1)] font-medium">
                  {labelOf(addingPlatform)}
                </li>
              ) : (proposedPlatforms || []).length === 0 ? (
                <li className="text-sm text-[color:var(--text-3)]">(none)</li>
              ) : (
                (proposedPlatforms || []).map((k) => (
                  <li key={k} className="text-sm text-[color:var(--text-1)] font-medium">
                    {labelOf(k)}
                  </li>
                ))
              )}
              {isOAuthAdd ? (
                <li className="text-[11px] text-[color:var(--text-3)] mt-1">
                  One current platform will be replaced when you complete the connect.
                </li>
              ) : null}
            </ul>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-3 text-xs text-[color:var(--text-2)] leading-relaxed">
          After confirming, this counts as your one Starter platform change for the current
          billing cycle. Publishing to the new set starts right away.
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={saving}
            className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
            data-testid="confirm-platform-change-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={saving}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            data-testid="confirm-platform-change-confirm"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Confirm platform change
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Phase 17d — hard-block dialog shown when a Starter user tries to
 * change platforms after they've already used their one-per-cycle
 * allowance. Read-only: they can Close or click Upgrade to Pro.
 */
function PlatformChangeBlockedDialog({ open, onClose, nextResetAt, navigate }) {
  if (!open) return null;
  const reset = formatResetDate(nextResetAt);
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="platform-change-blocked-dialog"
    >
      <div
        className="w-full max-w-md bg-[color:var(--bg-1)] border border-[color:var(--border-subtle)] rounded-xl p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md flex items-center justify-center shrink-0 bg-[color:rgba(255,138,61,0.14)] text-[color:var(--accent-warm)]">
            <Lock className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
              Platform change already used
            </div>
            <p className="text-sm text-[color:var(--text-2)] mt-1.5 leading-relaxed">
              You&apos;ve already changed your Starter platforms this billing cycle. You can change
              them again on{" "}
              <span className="font-semibold text-[color:var(--text-1)]">{reset}</span>, or upgrade
              to Pro for all platforms.
            </p>
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
            data-testid="platform-change-blocked-close"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              onClose();
              navigate("/billing");
            }}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            data-testid="platform-change-blocked-upgrade"
          >
            Upgrade to Pro
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Reusable modal for the "choose your N platforms" flow. Called from
 * both the needs-selection banner AND the everyday "Manage platforms"
 * button. Submits to POST /api/user/platform-slots.
 *
 * Phase 17d — this dialog now cooperates with the backend gate:
 *   • First save attempt sends {confirm_consume: false}.
 *   • If the backend replies 409 platform_change_confirm, we open
 *     ConfirmPlatformChangeDialog. The user's Confirm resends the same
 *     payload with {confirm_consume: true}.
 *   • If the backend replies 409 platform_change_limit, we open
 *     PlatformChangeBlockedDialog.
 */
function ManagePlatformsDialog({ open, onOpenChange, slotCtx, onSaved, navigate }) {
  // ManagePlatformsDialog is only meaningful for capped plans. With
  // Starter now uncapped, this dialog is effectively Free-only, but
  // keep a numeric fallback for the dialog copy in case an unknown
  // future plan hits this code path.
  const cap = slotCtx?.platform_limit ?? 1;
  const connected = slotCtx?.connected_platforms || [];
  const initialActive = slotCtx?.active_platforms || [];
  const [picked, setPicked] = React.useState(initialActive);
  const [saving, setSaving] = React.useState(false);
  const [confirmState, setConfirmState] = React.useState(null); // { current, proposed, nextResetAt }
  const [blockedState, setBlockedState] = React.useState(null); // { nextResetAt }

  React.useEffect(() => {
    if (open) setPicked(slotCtx?.active_platforms || []);
  }, [open, slotCtx]);

  if (!open) return null;

  const toggle = (key) => {
    setPicked((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= cap) {
        toast.info(`You can only pick ${cap} platform${cap === 1 ? "" : "s"} on this plan.`);
        return prev;
      }
      return [...prev, key];
    });
  };

  const submit = async (confirmConsume) => {
    setSaving(true);
    try {
      await api.post("/user/platform-slots", {
        platforms: picked,
        ...(confirmConsume ? { confirm_consume: true } : {}),
      });
      toast.success("Platforms updated");
      setConfirmState(null);
      onSaved && (await onSaved());
      onOpenChange(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || {};
      const blockedBy = typeof detail === "object" ? detail.blocked_by : null;
      if (status === 409 && blockedBy === "platform_change_confirm") {
        // Open the confirmation sub-dialog. Nothing was written yet.
        setConfirmState({
          current: detail.current_active_platforms || slotCtx?.active_platforms || [],
          proposed: detail.requested_platforms || picked,
          nextResetAt: detail.next_reset_at || slotCtx?.platform_change?.next_reset_at,
        });
      } else if (status === 409 && blockedBy === "platform_change_limit") {
        setBlockedState({
          nextResetAt: detail.next_reset_at || slotCtx?.platform_change?.next_reset_at,
        });
      } else {
        toast.error(
          typeof detail === "string" ? detail : detail?.message || "Could not save platforms"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
        onClick={() => onOpenChange(false)}
        data-testid="manage-platforms-dialog"
      >
        <div
          className="w-full max-w-md bg-[color:var(--bg-1)] border border-[color:var(--border-subtle)] rounded-xl p-5 sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
            Choose your {cap} active platform{cap === 1 ? "" : "s"}
          </div>
          <p className="text-sm text-[color:var(--text-2)] mt-1.5 leading-relaxed">
            Your connected accounts stay saved. Pick which {cap} to keep active for publishing.
          </p>
          {slotCtx?.platform_change?.applies ? (
            <div
              className={
                "mt-3 rounded-md border p-2.5 text-[11px] leading-relaxed " +
                (slotCtx.platform_change.used
                  ? "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] text-[color:var(--text-2)]"
                  : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)]")
              }
              data-testid="manage-platforms-change-badge"
            >
              {(() => {
                const pc = slotCtx.platform_change;
                const cap = pc.cap ?? 2;
                const rem = typeof pc.remaining === "number" ? pc.remaining : (pc.used ? 0 : cap);
                const reset = formatResetDate(pc.next_reset_at);
                if (rem === 0) {
                  return `You've used both platform changes for this billing cycle. Your current platforms stay active until ${reset}.`;
                }
                if (rem === 1) {
                  return `You have 1 platform change remaining this billing cycle (resets ${reset}).`;
                }
                return `You have ${rem} platform changes remaining this billing cycle (resets ${reset}).`;
              })()}
            </div>
          ) : null}
          <div className="mt-4 space-y-2">
            {connected.length === 0 ? (
              <div className="text-sm text-[color:var(--text-3)] py-4 text-center">
                You don&apos;t have any platforms connected yet. Close this and connect one first.
              </div>
            ) : (
              connected.map((key) => {
                const isOn = picked.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggle(key)}
                    data-testid={`manage-platforms-toggle-${key}`}
                    className={
                      "w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition text-left " +
                      (isOn
                        ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                        : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                    }
                  >
                    <span className="text-sm font-medium">{PLATFORM_LABEL[key] || key}</span>
                    <span
                      className={
                        "h-5 w-5 rounded-full border flex items-center justify-center " +
                        (isOn
                          ? "border-[color:var(--brand-primary)] bg-[color:var(--brand-primary)] text-[color:var(--bg-0)]"
                          : "border-[color:var(--border-strong)]")
                      }
                    >
                      {isOn ? <Check className="h-3 w-3" /> : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          <div className="mt-3 text-[11px] text-[color:var(--text-3)]">
            {picked.length} of {cap} selected. Facebook and Instagram each count as 1.
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
              data-testid="manage-platforms-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => submit(false)}
              disabled={saving || picked.length === 0}
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              data-testid="manage-platforms-save"
            >
              {saving && !confirmState ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save selection
            </Button>
          </div>
        </div>
      </div>

      <ConfirmPlatformChangeDialog
        open={!!confirmState}
        onCancel={() => !saving && setConfirmState(null)}
        onConfirm={() => submit(true)}
        saving={saving}
        currentPlatforms={confirmState?.current || []}
        proposedPlatforms={confirmState?.proposed || []}
        nextResetAt={confirmState?.nextResetAt}
      />

      <PlatformChangeBlockedDialog
        open={!!blockedState}
        onClose={() => setBlockedState(null)}
        nextResetAt={blockedState?.nextResetAt}
        navigate={navigate}
      />
    </>
  );
}

/**
 * Convert a structured 402 platform_limit response into a clean toast.
 * Used everywhere the frontend triggers an action that could hit the
 * platform-slot gate.
 */
function handlePlatformLimitError(err, navigate) {
  const detail = err?.response?.data?.detail;
  const isPlatformLimit =
    err?.response?.status === 402 &&
    detail &&
    typeof detail === "object" &&
    detail.blocked_by === "platform_limit";
  if (!isPlatformLimit) return false;
  const msg =
    detail.message ||
    `You've used all ${detail.platform_limit} platform slots on your ${planLabel(detail.plan)} plan.`;
  toast.error(msg, {
    action: {
      label: "Manage platforms",
      onClick: () => navigate("/settings/connections"),
    },
  });
  return true;
}

function PlatformIcon({ p }) {
  const Comp = ICONS[p.icon];
  return (
    <div
      className="h-10 w-10 rounded-md flex items-center justify-center"
      style={{ background: `${p.color}25`, color: p.color }}
    >
      {Comp ? <Comp className="h-5 w-5" /> : null}
    </div>
  );
}

function CustomerStatusBadge({ p }) {
  // LinkedIn-only override: when the token has expired or the row is
  // otherwise flagged for reauthentication, the pill must NOT use the
  // success (green) tone even though the DB still has connected=true.
  // The label already reads "Reconnect needed" via customer_status_label;
  // this brings the color in line with the message.
  const isLinkedInReauth =
    (p.platform || "").toLowerCase() === "linkedin" &&
    (p.needs_reauth === true || p.is_expired === true);
  const isConnected = p.connected && !isLinkedInReauth;
  const isComingSoon = p.coming_soon || p.awaiting_app_review;
  const isUnavailable = !p.connected && !isComingSoon && !p.configured;

  const tone = isLinkedInReauth
    ? "bg-[color:rgba(255,138,61,0.14)] text-[color:var(--accent-warm)]"
    : isConnected
    ? "bg-[color:rgba(46,204,113,0.12)] text-[color:var(--success)]"
    : isComingSoon
    ? "bg-[color:rgba(255,138,61,0.10)] text-[color:var(--accent-warm)]"
    : isUnavailable
    ? "bg-[color:rgba(255,138,61,0.12)] text-[color:var(--accent-warm)]"
    : "bg-[color:var(--surface-3)] text-[color:var(--text-2)]";

  const Icon = isLinkedInReauth
    ? AlertCircle
    : isConnected
    ? Check
    : isComingSoon
    ? Clock
    : isUnavailable
    ? AlertCircle
    : null;

  return (
    <div
      className={"text-xs inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 " + tone}
      data-testid={`connection-status-${p.platform.toLowerCase()}`}
    >
      {Icon ? <Icon className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {p.customer_status_label || (isConnected ? "Connected" : isComingSoon ? "Coming soon" : "Not connected")}
    </div>
  );
}

function CustomerPlatformCard({ p, onConnect, onDisconnect, onReconnectLinkedIn, onManage, busy, isAdmin, slotCtx }) {
  // Phase 17: derive per-card active-vs-inactive label from slotCtx.
  const canon = (p.platform || p.icon || "").toLowerCase();
  const slotState = (() => {
    if (!slotCtx || slotCtx.unlimited) return null; // unlimited plans skip the badge
    const active = slotCtx.active_platforms || [];
    const connected = slotCtx.connected_platforms || [];
    if (active.includes(canon)) return "active";
    if (connected.includes(canon) || p.connected) return "inactive";
    return "not_connected";
  })();
  const lower = p.platform.toLowerCase();
  const supportedHere = SUPPORTED_FOR_CUSTOMER_CONNECT.has(lower);
  // Treat awaiting_app_review like coming_soon for normal customers; admins
  // can still open the dialog (which surfaces demo mode + debug panel).
  const isAwaitingReview = !!p.awaiting_app_review;
  const reviewLocked = isAwaitingReview && !isAdmin;
  const canConnect = !p.connected && !p.coming_soon && !reviewLocked && p.configured && supportedHere;
  const showUnavailable = !p.connected && !p.coming_soon && !reviewLocked && !p.configured;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5"
      data-testid={`connection-card-${lower}`}
    >
      <div className="flex items-start gap-3">
        <PlatformIcon p={p} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
              {p.label}
            </div>
            {p.coming_soon ? (
              <span
                className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:var(--surface-3)] text-[color:var(--text-2)] border border-[color:var(--border-subtle)]"
                data-testid={`coming-soon-chip-${lower}`}
              >
                {lower === "linkedin" ? "Planned" : "Coming soon"}
              </span>
            ) : isAwaitingReview ? (
              <span
                className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:rgba(255,138,61,0.14)] text-[color:var(--accent-warm)] border border-[color:rgba(255,138,61,0.35)]"
                data-testid={`app-review-chip-${lower}`}
              >
                Requires Meta App Review
              </span>
            ) : null}
            {slotState === "active" ? (
              <span
                className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:rgba(46,204,113,0.12)] text-[color:var(--success)] border border-[color:rgba(46,204,113,0.35)]"
                data-testid={`slot-badge-active-${lower}`}
              >
                Active for your plan
              </span>
            ) : slotState === "inactive" ? (
              <span
                className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:var(--surface-3)] text-[color:var(--text-3)] border border-[color:var(--border-subtle)]"
                data-testid={`slot-badge-inactive-${lower}`}
                title="Your account is still connected, but not counted toward your active plan slots."
              >
                Connected but inactive
              </span>
            ) : null}
            {/*
              LinkedIn-only soft-expiry chip. LinkedIn access tokens
              expire ~60 days after connect and cannot be refreshed with
              the current scope set, so we surface a warning ahead of
              time. Never rendered for other platforms.
            */}
            {lower === "linkedin" && p.soft_expiry_warning && !p.needs_reauth ? (
              <span
                className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:rgba(255,138,61,0.12)] text-[color:var(--accent-warm)] border border-[color:rgba(255,138,61,0.35)]"
                data-testid="linkedin-soft-expiry-chip"
                title="Your LinkedIn access token is about to expire. Reconnect from this card to avoid interrupted publishing."
              >
                {typeof p.days_until_expiry === "number"
                  ? p.days_until_expiry <= 0
                    ? "Expires today"
                    : `Expires in ${p.days_until_expiry} ${p.days_until_expiry === 1 ? "day" : "days"}`
                  : "Expires soon"}
              </span>
            ) : null}
          </div>
          <div className="text-xs text-[color:var(--text-3)] mt-1">{p.description}</div>
          <div className="mt-3">
            <CustomerStatusBadge p={p} />
          </div>
          <div className="text-[11px] text-[color:var(--text-2)] mt-2 leading-relaxed">
            {p.customer_status_message || p.status_message}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {p.coming_soon ? (
          <Button
            size="sm"
            disabled
            className="bg-[color:var(--surface-2)] text-[color:var(--text-3)] cursor-not-allowed"
            data-testid={`btn-comingsoon-${lower}`}
          >
            <Clock className="h-3.5 w-3.5 mr-1.5" /> {lower === "linkedin" ? "Planned" : "Coming soon"}
          </Button>
        ) : reviewLocked ? (
          <Button
            size="sm"
            disabled
            className="bg-[color:var(--surface-2)] text-[color:var(--text-3)] cursor-not-allowed"
            data-testid={`btn-awaiting-review-${lower}`}
          >
            <Clock className="h-3.5 w-3.5 mr-1.5" /> Requires Meta approval
          </Button>
        ) : showUnavailable ? (
          <Button
            size="sm"
            disabled
            className="bg-[color:var(--surface-2)] text-[color:var(--text-3)] cursor-not-allowed"
            data-testid={`btn-unavailable-${lower}`}
          >
            <AlertCircle className="h-3.5 w-3.5 mr-1.5" /> Temporarily unavailable
          </Button>
        ) : p.connected ? (
          <>
            {/*
              LinkedIn-only: when the connection is expired /
              needs_reauth, promote the reconnect action to a prominent
              primary button on the card itself so users don't need to
              open Manage first. Uses the same onConnect(p) handler as
              the Manage flow — no new OAuth endpoint, no new state.
            */}
            {lower === "linkedin" && (p.needs_reauth === true || p.is_expired === true) ? (
              <>
                <Button
                  size="sm"
                  onClick={() => onReconnectLinkedIn(p)}
                  disabled={busy || !supportedHere}
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                  data-testid="btn-reconnect-linkedin"
                >
                  Reconnect LinkedIn
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDisconnect(p)}
                  disabled={busy}
                  className="text-xs text-[color:var(--danger)] hover:text-[color:var(--danger)]"
                  data-testid={`btn-disconnect-${lower}`}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <>
                {/* Phase 18 — Make active CTA for a connected-but-
                    inactive platform. Opens Manage Platforms so the
                    user can pick this canon as one of their N active
                    slots. Never silently deactivates another platform
                    — the dialog requires the user to make the swap
                    themselves. Rendered inline with Manage so both
                    surfaces stay discoverable. */}
                {slotState === "inactive" && onManage ? (
                  <Button
                    size="sm"
                    onClick={() => onManage(p)}
                    disabled={busy}
                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                    data-testid={`btn-make-active-${lower}`}
                  >
                    Make active
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onConnect(p)}
                  disabled={busy || !supportedHere}
                  className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                  data-testid={`btn-manage-${lower}`}
                >
                  Manage
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDisconnect(p)}
                  disabled={busy}
                  className="text-xs text-[color:var(--danger)] hover:text-[color:var(--danger)]"
                  data-testid={`btn-disconnect-${lower}`}
                >
                  Disconnect
                </Button>
              </>
            )}
          </>
        ) : canConnect ? (
          <Button
            size="sm"
            onClick={() => onConnect(p)}
            disabled={busy}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            data-testid={`btn-connect-${lower}`}
          >
            Connect
          </Button>
        ) : (
          <Button
            size="sm"
            disabled
            className="bg-[color:var(--surface-2)] text-[color:var(--text-3)] cursor-not-allowed"
            data-testid={`btn-disabled-${lower}`}
          >
            Not available
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default function ConnectionsPage() {
  const { isAdmin, isAdminEligible, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  // ?view=admin (only honored if user is admin) OR hidden /settings/admin/connections route
  const wantsAdminView =
    location.pathname.startsWith("/settings/admin/connections") || params.get("view") === "admin";
  const [tab, setTab] = useState(wantsAdminView && isAdmin ? "admin" : "customer");
  const [unlockOpen, setUnlockOpen] = useState(false);

  useEffect(() => {
    // Eligible user lands on the admin URL → automatically prompt for passcode
    if (wantsAdminView && isAdminEligible && !isAdmin) {
      setUnlockOpen(true);
    }
    // Already unlocked → flip to admin tab
    if (wantsAdminView && isAdmin) setTab("admin");
    // Non-eligible user shouldn't see "?view=admin" — strip the query.
    if (wantsAdminView && !isAdminEligible && !location.pathname.startsWith("/settings/admin/connections")) {
      const next = new URLSearchParams(params);
      next.delete("view");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAdminView, isAdmin, isAdminEligible]);

  const switchToAdminTab = () => {
    if (isAdmin) {
      setTab("admin");
    } else if (isAdminEligible) {
      setUnlockOpen(true);
    } else {
      toast.error("Admin access isn't available for this account.");
    }
  };

  const lockAdmin = async () => {
    try {
      await api.post("/admin/lock");
      await refreshAuth();
      setTab("customer");
      toast.success("Admin mode locked");
    } catch (_e) {
      toast.error("Could not lock admin mode");
    }
  };

  const [platforms, setPlatforms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialogPlatform, setDialogPlatform] = useState(null);
  // Phase 17 platform-slot state — always driven by the /connections
  // response so we stay in sync with the backend on every mount / refresh.
  const [slotCtx, setSlotCtx] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [choosePlatformOpen, setChoosePlatformOpen] = useState(false);
  // Phase 17d.1 — shared confirm / blocked dialogs for the OAuth start
  // 409 handshake. Populated from CustomerConnectionDialog callbacks.
  const [connectConfirm, setConnectConfirm] = useState(null); // { detail, retry }
  const [connectBlocked, setConnectBlocked] = useState(null); // { nextResetAt }

  const load = async () => {
    try {
      const res = await api.get("/connections");
      const backendPlatforms = res.data?.platforms || [];
      const backendKeys = new Set(
        backendPlatforms.map((bp) => (bp.platform || "").toLowerCase())
      );
      // Append client-only roadmap cards for TikTok, YouTube,
      // Google Business Profile, and Pinterest. Deduped so the moment the backend
      // ever surfaces a real entry for any of these, the client-only
      // placeholder drops out automatically.
      const roadmap = UPCOMING_INTEGRATIONS.filter(
        (u) => !backendKeys.has(u.platform)
      );
      setPlatforms([...backendPlatforms, ...roadmap]);
      setSlotCtx(res.data?.platform_slots || null);
    } catch (_e) {
      /* ignore */
    }
  };

  // Phase 18: connect-vs-active-slot separation. The active-slot cap
  // no longer blocks OAuth start — users can save additional platform
  // connections while they exceed their plan's active-slot budget, and
  // the newly connected platform stays connected-but-inactive until
  // the user picks it in Manage Platforms. Coming Soon / awaiting-
  // review / not-configured guards are still enforced by the individual
  // card ``canConnect`` boolean upstream, so this handler stays a
  // straight pass-through.
  const attemptConnect = (p, fallbackOpen) => fallbackOpen(p);

  useEffect(() => {
    load();
  }, []);

  // OAuth callback toasts (Facebook + X)
  useEffect(() => {
    const fb = params.get("fb");
    const x = params.get("x");
    const reason = params.get("reason") || "";
    let changed = false;
    if (fb) {
      changed = true;
      if (fb === "connected") {
        toast.success(
          params.get("auto_selected") ? "Facebook connected — Page auto-selected!" : "Facebook connected!"
        );
        setDialogPlatform("facebook");
      } else if (fb === "connected_basic") {
        toast.success("Facebook account linked! Enable Page publishing to start posting.");
        setDialogPlatform("facebook");
      } else if (fb === "connected_discovery") {
        toast.success("Facebook connected — we'll auto-detect your Instagram Business account.");
        setDialogPlatform("facebook");
      } else if (fb === "scopes_partial") {
        const missing = (params.get("missing") || "").split(",").filter(Boolean);
        const friendly = missing
          .map((m) => ({
            pages_show_list: "Pages list",
            pages_read_engagement: "Page details",
            instagram_basic: "Instagram",
            pages_manage_posts: "Page publishing",
            instagram_content_publish: "Instagram publishing",
          }[m] || m))
          .join(", ");
        toast.warning(
          friendly
            ? `Facebook connected, but didn't share: ${friendly}. You can re-grant from Facebook → Apps → Postmoro.`
            : "Facebook connected with limited permissions.",
          { duration: 7000 }
        );
        setDialogPlatform("facebook");
      } else if (fb === "scopes_unavailable") {
        toast.error(
          "Facebook didn't grant Page or Instagram access. This usually means Meta App Review is required for these permissions, or you skipped them in the consent screen. Disconnect and try again, approving every requested permission.",
          { duration: 8000 }
        );
        setDialogPlatform("facebook");
      } else if (fb === "no_pages") {
        toast.error("Connected, but no Facebook Pages were found on your account.");
        setDialogPlatform("facebook");
      } else if (fb === "error") {
        if (reason === "user_denied") {
          toast.error("Facebook connection cancelled.");
        } else if (reason === "invalid_state") {
          toast.error("That connection request expired. Please try again.");
        } else {
          toast.error("We couldn't connect Facebook. Please try again.");
        }
      }
    }
    if (x) {
      changed = true;
      if (x === "connected") {
        toast.success("X / Twitter connected!");
        setDialogPlatform("x");
      } else if (x === "error") {
        toast.error("We couldn't connect your X / Twitter account. Please try again.");
      }
    }
    const linkedin = params.get("linkedin");
    if (linkedin) {
      changed = true;
      if (linkedin === "connected") {
        toast.success("LinkedIn connected!");
        setDialogPlatform("linkedin");
      } else if (linkedin === "error") {
        const reasonMap = {
          user_denied: "LinkedIn connection cancelled.",
          invalid_state: "That LinkedIn connection request expired. Please try again.",
          token_exchange_failed: "LinkedIn rejected the connection. Try again, and make sure your app has the required products approved.",
          no_member_urn: "Connected to LinkedIn, but we couldn't read your member profile. Try reconnecting.",
        };
        toast.error(reasonMap[reason] || "We couldn't connect your LinkedIn account. Please try again.");
      }
    }
    if (changed) {
      const next = new URLSearchParams(params);
      ["fb", "x", "linkedin", "reason", "auto_selected"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const openConnect = (p) => {
    const lower = p.platform.toLowerCase();
    if (SUPPORTED_FOR_CUSTOMER_CONNECT.has(lower)) setDialogPlatform(lower);
  };

  // Map every supported platform to its disconnect endpoint. Centralising
  // this prevents the bug where adding a new platform (LinkedIn) silently
  // falls off the if/else chain and the toast happily lies that we
  // disconnected when no API call was dispatched.
  const DISCONNECT_ENDPOINTS = {
    facebook: "/connections/facebook/disconnect",
    instagram: "/connections/instagram/disconnect-v2",
    x: "/connections/x/disconnect",
    linkedin: "/connections/linkedin/disconnect",
  };

  const disconnect = async (p) => {
    if (!window.confirm(`Disconnect ${p.label}?`)) return;
    const lower = p.platform.toLowerCase();
    const endpoint = DISCONNECT_ENDPOINTS[lower];
    if (!endpoint) {
      toast.error(`Disconnect for ${p.label} is not available.`);
      return;
    }
    setBusy(true);
    try {
      await api.post(endpoint);
      toast.success(`${p.label} disconnected`);
      await load();
    } catch (_e) {
      toast.error("Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  // Count LinkedIn as actively connected only while its token is usable.
  // An expired / needs_reauth LinkedIn row keeps connected=true in the DB
  // but must NOT be surfaced as an actively-connected slot in the top
  // Connections summary — the user cannot publish to it until they
  // reconnect. Other platforms are counted using the raw ``connected``
  // flag exactly as before.
  const isPlatformActivelyConnected = (p) => {
    if (!p.connected) return false;
    if ((p.platform || "").toLowerCase() === "linkedin" &&
        (p.needs_reauth === true || p.is_expired === true)) {
      return false;
    }
    return true;
  };
  const connectedCount = useMemo(
    () => platforms.filter(isPlatformActivelyConnected).length,
    [platforms]
  );

  // Direct LinkedIn reconnect handler — wired to the expired-state
  // primary CTA on the card. Uses the shared ``startLinkedInOAuth``
  // helper so the click hands off to LinkedIn's authorize screen with
  // ZERO intermediate UI (no Manage dialog). On 409 we fall back to
  // the existing Manage dialog which already knows how to render the
  // platform-change confirmation, so users are never dead-ended. The
  // existing LinkedIn connection row is untouched here — only the
  // OAuth callback rotates tokens on success.
  const handleDirectReconnectLinkedIn = async (p) => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await startLinkedInOAuth();
      if (result && result.ok) {
        // Browser is navigating to LinkedIn — nothing else to do.
        return;
      }
      if (result && result.code === "platform_change_confirm") {
        // Rare: the once-per-cycle platform-change gate is engaged.
        // Delegate to the existing dialog which already knows how to
        // render the platform-change confirmation UI.
        openConnect(p);
        return;
      }
      if (result && result.code === "platform_change_limit") {
        openConnect(p);
        return;
      }
      toast.error(
        (result && result.human) ||
          "We couldn't start the LinkedIn reconnect. Please try again."
      );
    } catch (_e) {
      toast.error(
        "We couldn't start the LinkedIn reconnect. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };
  const activeCount = useMemo(
    () => platforms.filter((p) => !p.coming_soon).length,
    [platforms]
  );

  return (
    <div className="relative min-h-screen p-6 md:p-8" data-testid="connections-page">
      <GradientMesh />
      <PageHeader
        title="Connections"
        subtitle="Connect the social accounts you want Postmoro to publish to. We'll handle scheduling and posting for you."
        action={
          <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="connections-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
          </Button>
        }
      />

      {/* Tabs — eligible admins see both; customers see nothing here */}
      {isAdminEligible ? (
        <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="connections-tabs">
          <button
            type="button"
            onClick={() => setTab("customer")}
            className={
              "text-sm px-3 py-1.5 rounded-md transition border " +
              (tab === "customer"
                ? "bg-[color:var(--surface-2)] border-[color:var(--brand-primary)] text-[color:var(--text-1)]"
                : "bg-transparent border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
            }
            data-testid="connections-tab-customer"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1.5 inline" /> Customer view
          </button>
          <button
            type="button"
            onClick={switchToAdminTab}
            className={
              "text-sm px-3 py-1.5 rounded-md transition border " +
              (tab === "admin"
                ? "bg-[color:var(--surface-2)] border-[color:var(--brand-primary)] text-[color:var(--text-1)]"
                : "bg-transparent border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
            }
            data-testid="connections-tab-admin"
          >
            {isAdmin ? (
              <LockOpen className="h-3.5 w-3.5 mr-1.5 inline text-[color:var(--success)]" />
            ) : (
              <Lock className="h-3.5 w-3.5 mr-1.5 inline" />
            )}
            Admin setup
            {!isAdmin ? (
              <span className="ml-2 text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Locked</span>
            ) : null}
          </button>
          {isAdmin && tab === "admin" ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={lockAdmin}
              className="text-xs text-[color:var(--accent-warm)]"
              data-testid="admin-lock-button"
            >
              <Lock className="h-3 w-3 mr-1" /> Lock admin
            </Button>
          ) : null}
        </div>
      ) : null}

      {tab === "admin" && isAdmin ? (
        <div className="mt-6 relative">
          <AdminConnectionsPanel onChanged={() => load()} />
        </div>
      ) : (
        <>
          {/* Summary strip */}
          <GlassCard
            className="px-5 py-3 mt-6 flex items-center gap-4 flex-wrap"
            data-testid="connections-summary"
          >
            <div className="text-xs">
              <span className="text-[color:var(--text-3)] uppercase tracking-wider">Connected </span>
              <span className="text-[color:var(--text-1)] font-display text-base tabular-nums">
                {connectedCount}
              </span>
              <span className="text-[color:var(--text-3)]"> / {activeCount}</span>
            </div>
            <div className="text-xs text-[color:var(--text-3)] flex items-center gap-2 flex-wrap">
              {platforms.map((p) => (
                <span key={p.platform} className="inline-flex items-center gap-1">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{
                      background: p.connected
                        ? "var(--success)"
                        : p.coming_soon
                        ? "var(--text-3)"
                        : "var(--accent-warm)",
                    }}
                  />
                  {p.label}
                </span>
              ))}
            </div>
          </GlassCard>

          <PlatformSlotStrip
            slotCtx={slotCtx}
            navigate={navigate}
            onManage={() => setManageOpen(true)}
            onChoosePlatform={(idx, currentCanon) => {
              // Currently occupied slot → open the Manage dialog so user
              // can deactivate/replace. Empty slot → open the Choose
              // Platform selector to pick a new one to connect.
              if (currentCanon) {
                setManageOpen(true);
              } else {
                setChoosePlatformOpen(true);
              }
            }}
            platforms={platforms}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5 relative">
            {platforms.map((p) => (
              <CustomerPlatformCard
                key={p.platform}
                p={p}
                onConnect={(pp) => attemptConnect(pp, openConnect)}
                onDisconnect={disconnect}
                onReconnectLinkedIn={handleDirectReconnectLinkedIn}
                onManage={() => setManageOpen(true)}
                busy={busy}
                isAdmin={isAdmin}
                slotCtx={slotCtx}
              />
            ))}
          </div>

          <div className="mt-6 text-xs text-[color:var(--text-3)] max-w-2xl leading-relaxed">
            Need a different account or having trouble?{" "}
            <span className="text-[color:var(--text-2)]">
              Contact support and we'll help you connect.
            </span>
          </div>
        </>
      )}

      {/* Customer-facing dialog (no developer info) */}
      <CustomerConnectionDialog
        open={!!dialogPlatform}
        onOpenChange={(v) => setDialogPlatform(v ? dialogPlatform : null)}
        platform={dialogPlatform}
        onChanged={() => load()}
        onPlatformChangeConfirm={({ detail, retry }) => {
          setConnectConfirm({ detail, retry });
        }}
        onPlatformChangeBlocked={({ detail }) => {
          setConnectBlocked({ nextResetAt: detail?.next_reset_at });
        }}
      />

      {/* Phase 17c platform-management dialog */}
      <ManagePlatformsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        slotCtx={slotCtx}
        onSaved={load}
        navigate={navigate}
      />

      {/* Phase 17d.1 — shared confirm dialog for OAuth-start swaps */}
      <ConfirmPlatformChangeDialog
        open={!!connectConfirm}
        onCancel={() => setConnectConfirm(null)}
        onConfirm={async () => {
          const retry = connectConfirm?.retry;
          setConnectConfirm(null);
          if (retry) await retry();
        }}
        saving={false}
        currentPlatforms={connectConfirm?.detail?.current_active_platforms || []}
        proposedPlatforms={connectConfirm?.detail?.requested_platforms || []}
        nextResetAt={connectConfirm?.detail?.next_reset_at}
        addingPlatform={connectConfirm?.detail?.adding_platform}
      />

      {/* Phase 17d.1 — shared blocked dialog for OAuth-start swaps */}
      <PlatformChangeBlockedDialog
        open={!!connectBlocked}
        onClose={() => setConnectBlocked(null)}
        nextResetAt={connectBlocked?.nextResetAt}
        navigate={navigate}
      />

      {/* Phase 17c: Choose Platform selector (empty-slot flow) */}
      <ChoosePlatformDialog
        open={choosePlatformOpen}
        onOpenChange={setChoosePlatformOpen}
        platforms={platforms}
        slotCtx={slotCtx}
        onConnect={(p) => {
          setChoosePlatformOpen(false);
          openConnect(p);
        }}
        onActivate={async (canon) => {
          // Activate a connected-but-inactive platform by pinning slots
          const current = slotCtx?.active_platforms || [];
          try {
            await api.post("/user/platform-slots", {
              platforms: [...current, canon],
            });
            toast.success("Platform activated");
            setChoosePlatformOpen(false);
            await load();
          } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not activate platform");
          }
        }}
      />

      {/* Admin passcode unlock (eligible admins only) */}
      <AdminUnlockDialog
        open={unlockOpen}
        onOpenChange={setUnlockOpen}
        onUnlocked={() => {
          setTab("admin");
        }}
      />
    </div>
  );
}
